import axios, { AxiosError, AxiosResponse } from 'axios'
import { searchJobs, searchJobsCount } from '@/api/jobs'
import { computed, Ref, ref, unref, UnwrapNestedRefs, watchEffect } from 'vue'
import moment, { isMoment } from 'moment/moment'
import { ACTIVE, CANCELLED, COMPLETED, NO_APPROVAL, PAUSED } from '@/models/job'
import { SearchJobsCountQuery, SearchJobsQuery } from '@/components/jobs/query'
import { useQuery } from '@tanstack/vue-query'
import { JobSearchResponse } from '@/models/types/job-search-response'

type MaybeRef<T> = Ref<T> | T

export function useJobsSearch(
  query: MaybeRef<UnwrapNestedRefs<SearchJobsQuery>>,
  options?: { enabled: boolean | Ref<boolean> },
) {
  const queryParams = computed(() => standardizeQueryParams(query))
  const lastUpdatedTime = ref()
  const lastUpdatedTimeAgo = ref()
  const enabled = computed(() => unref(options?.enabled) ?? true)

  const queryKey = computed(() => ['jobs', 'search', queryParams.value])

  const { data, error, isLoading, isFetched, isFetching, refetch, isRefetching } = useQuery<
    AxiosResponse,
    AxiosError,
    AxiosResponse,
    any
  >({
    queryKey,
    queryFn: ({ signal }) => {
      const CancelToken = axios.CancelToken
      const source = CancelToken.source()

      const q = unref(query)
      if (
        'approvalStatuses' in q &&
        (q.approvalStatuses?.length === 0 ||
          q.divisions?.length === 0 ||
          q.priorities?.length === 0 ||
          q.serviceProviders?.length === 0 ||
          q.resourceTypes?.length === 0 ||
          q.statuses?.length === 0)
      ) {
        return { data: { data: [], meta: { total_pages: 0, total: 0 } } }
      }

      const promise = searchJobs(queryParams.value, source.token)

      signal?.addEventListener('abort', () => {
        source.cancel('Query was cancelled by TanStack Query')
      })

      return promise
    },
    retry: false,
    keepPreviousData: true,
    enabled,
    refetchInterval: computed(() =>
      enabled.value ? moment.duration(30, 'seconds').asMilliseconds() : false,
    ),
  })

  watchEffect(() => {
    if (data.value && !isLoading.value) {
      lastUpdatedTime.value = moment()
    }
  })

  watchEffect((onCleanup) => {
    if (!enabled.value) return
    const interval = setInterval(() => {
      if (lastUpdatedTime.value) {
        lastUpdatedTimeAgo.value = lastUpdatedTime.value.fromNow()
      }
    }, 1000)

    onCleanup(() => {
      clearInterval(interval)
    })
  })

  const jobs = computed<JobSearchResponse[]>(() => {
    return Object.freeze(
      (data?.value?.data?.data ?? []).map((j) => {
        return {
          ...j,
          estimatedDuration: moment.duration(
            moment(j.requested_end).diff(moment(j.requested_start)),
          ),
          startTime: j.requested_start,
          endTime: j.requested_end,
          approvalStatus: j.approval_status ?? NO_APPROVAL,
          get estimatedCompletedTime() {
            return moment(j.active_time ?? j.requested_start).add(this.estimatedDuration)
          },
          get isOverdue() {
            return (
              j.status !== COMPLETED &&
              j.status !== CANCELLED &&
              moment(j.requested_start).isBefore()
            )
          },
          get isLate() {
            return (
              (j.status === ACTIVE || j.status === PAUSED) && this.estimatedCompletedTime.isBefore()
            )
          },
        }
      }),
    )
  })

  const hasNextPage = computed<boolean | undefined>(() => {
    return data?.value?.data?.meta?.has_next_page
  })

  return {
    jobs,
    hasNextPage,
    error,
    isLoading,
    isFetched,
    isFetching,
    isRefetching,
    reFetch: refetch,
    lastUpdatedTimeAgo,
  }
}

export function useJobsSearchCount(
  countQuery: MaybeRef<UnwrapNestedRefs<SearchJobsCountQuery>>,
  options?: { enabled: boolean | Ref<boolean> },
) {
  const queryParams = computed(() => standardizeQueryParams(countQuery))
  const queryKey = computed(() => ['jobs-count', queryParams.value])
  const enabled = computed(() => unref(options?.enabled) ?? true)

  const {
    data: countData,
    error: countError,
    isLoading: countLoading,
    isFetched: countFetched,
    isFetching: countFetching,
    refetch: countRefetch,
    isRefetching: countRefetching,
  } = useQuery<AxiosResponse<SearchCount>, AxiosError, AxiosResponse<SearchCount>, any>({
    queryKey,
    queryFn: ({ signal }) => {
      const CancelToken = axios.CancelToken
      const source = CancelToken.source()
      const q = unref(countQuery)

      if (
        'approvalStatuses' in q &&
        (q.approvalStatuses?.length === 0 ||
          q.divisions?.length === 0 ||
          q.priorities?.length === 0 ||
          q.serviceProviders?.length === 0 ||
          q.resourceTypes?.length === 0 ||
          q.statuses?.length === 0)
      ) {
        return { data: { data: [], meta: { total_pages: 0, total: 0 } } }
      }

      const promise = searchJobsCount(queryParams.value, source.token)

      signal?.addEventListener('abort', () => {
        source.cancel('Job search count query was cancelled by TanStack Query')
      })

      return promise
    },
    retry: false,
    keepPreviousData: true,
    enabled,
  })

  const count = computed<number>(() => {
    return countData.value?.data.count ?? 0
  })

  return {
    count,
    countError,
    countLoading,
    countFetched,
    countFetching,
    countRefetch,
    countRefetching,
  }
}

function standardizeQueryParams(
  query:
    | MaybeRef<UnwrapNestedRefs<SearchJobsQuery>>
    | MaybeRef<UnwrapNestedRefs<SearchJobsCountQuery>>,
) {
  const params: Record<string, unknown> = {}

  Object.entries(unref(query))
    .map(([key, value]) => [key, unref(value)] as const)
    .filter(([, value]) => {
      if (value === null || value === undefined) return false
      if (Array.isArray(value)) return value.length > 0
      return true
    })
    .forEach(([key, value]) => {
      if (isMoment(value)) {
        params[key] = value.toISOString()
      } else if (Array.isArray(value)) {
        params[key] = value
      } else {
        params[key] = value
      }
    })

  return params
}

class SearchCount {
  constructor(count: number) {
    this.count = count
  }

  count: number
}
